import React, { Component, createContext, forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';

import Icon from '@mui/material/Icon';

import Card from '../Card/Card';
import classes from './Carousel.scss';

class CarouselButton extends Component {
  static propTypes = {
    dir : PropTypes.string,
    icon: PropTypes.string
  };

  render() {
    const { dir, icon, onClick } = this.props;

    return (
      <button type="button" className={classNames(classes.button, { [classes[`button_${dir}`]]: dir })} onClick={onClick}>
        {icon && <Icon>{icon}</Icon>}
      </button>
    )
  }
}

export const DEFAULT_SLIDE_LIMITS = {
  0: 1
}

export default class Carousel extends Component {
  static propTypes = {
    className: PropTypes.string,
    items: PropTypes.array,
    limits: PropTypes.object,
    autoplay: PropTypes.bool,
    cardProps: PropTypes.object,
    isCardMini: PropTypes.bool,
    isCardExpandable: PropTypes.bool,
    isCardExpanded: PropTypes.bool,
    isStretched: PropTypes.bool,
    carouselSettings: PropTypes.object,
    spaceBetween: PropTypes.bool
  };

  static defaultProps = {
    isStretched: true,
    spaceBetween: true,
  }

  state = {
    slidesToShow: null
  };

  updateSlidesToShow() {
    if (!window) {
      return;
    }

    let windowWidth = window.innerWidth;
    let limits = this.props.limits || DEFAULT_SLIDE_LIMITS;
    let topWidthLimit = Math.max(...Object.keys(limits).filter(limit => windowWidth >= limit)) || 0;
    let slidesToShow = limits[topWidthLimit] || DEFAULT_SLIDE_LIMITS[0];

    this.setState({ slidesToShow });
  }

  resizeHandler = null;

  UNSAFE_componentWillMount() {
    const { limits } = this.props;

    this.setState({
      slidesToShow: (limits && limits[0]) || DEFAULT_SLIDE_LIMITS[0]
    });

    this.updateSlidesToShow();
  }

  componentDidMount() {
    if (!window) {
      return;
    }

    window.addEventListener('resize', this.resizeHandler = this.updateSlidesToShow.bind(this));
  }

  componentWillUnmount() {
    if (!window) {
      return;
    }

    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }

  slideElems = {};

  setSlideElem = (el) => {
    if (!el) { return; }

    this.slideElems = {
      ...this.slideElems,
      [el.index]: el,
    };
  };

  getIsOriginal = (el) => {
    return this.slideElems[el.index] === el;
  };

  render() {
    const { items, children, isStretched, className, cardProps, spaceBetween } = this.props;

    const cards = (items && items.length) ?
      items.map((item, index) => (
        <Card
          {...item}
          {...cardProps}
          isMini            = {this.props.isCardMini}
          expandable        = {this.props.isCardExpandable}
          initiallyExpanded = {this.props.isCardExpanded}
        >{item.text}</Card>
      )) :
      (children || [])
    ;

    const slides = cards.map((card, index, array) => (
      <CardWrapper key={index} index={index} spaceBetween={spaceBetween} ref={this.setSlideElem} getIsOriginal={this.getIsOriginal}>
        {card}
      </CardWrapper>
    ));

    const carouselSettings =  {
      className: classes.slider,
      dots: false,
      infinite: slides.length > 1, // workaround react-slick bug with single slide https://github.com/akiran/react-slick/issues/2405#issuecomment-2476619845
      autoplay: this.props.autoplay,
      speed: 300,
      cssEase: 'cubic-bezier(0.165, 0.840, 0.440, 1.000)',
      slidesToShow: this.state.slidesToShow,
      slidesToScroll: 1,
      arrows: true,
      prevArrow: <CarouselButton dir="prev" icon="chevron_left" />,
      nextArrow: <CarouselButton dir="next" icon="chevron_right" />,
      ...this.props.carouselSettings,
      unslick: slides.length <= this.state.slidesToShow,
    };

    return (
      <div className={classNames(classes.carousel, className, { [classes.carouselStretch]: isStretched })}>
        <Slider {...carouselSettings}>
          {slides}
        </Slider>
      </div>
    );
  }
}

export const SlideCardContext = createContext(true);

const CardWrapper = forwardRef(function CardWrapper({ children, index, spaceBetween, className, 'data-index': dataIndex, getIsOriginal, ...rest }, ref) {
  const handle = useMemo(() => ({
    index,
  }), [index]);

  useImperativeHandle(ref, () => {
    return handle;
  }, [handle]);

  const getHidePopup = useCallback(() => {
    return !getIsOriginal(handle);
  }, [handle, getIsOriginal]);

  return (
    <div className={classNames(className, classes.item)} data-index={dataIndex} {...rest}>
      <div className={classNames(classes.itemWrapper, { [classes.itemWrapperSpaceBetween]: spaceBetween })}>
        {/* React.cloneElement() somehow messes with event handler on close button in the article dialog, so we pass info through context here */}
        <SlideCardContext.Provider value={getHidePopup}>
          {children}
        </SlideCardContext.Provider>
      </div>
    </div>
  );
});
